import { useState, useEffect, useCallback, useRef } from 'react';
import { collection, addDoc, doc, updateDoc, deleteDoc, onSnapshot, query, orderBy, Timestamp, deleteField } from 'firebase/firestore';
import { db, appId } from '../firebase';
import { errorMessage } from '../helpers';
import { FREE_LIMITS, PRO_SNAPSHOT_LIMIT } from '../config/billing';
import { DEMO_MONETARY_FIELDS } from './useDemoData';
import { DEMO_CURRENCY_CONVERSION_RATES } from '../constants';

const toStartOfDay = (inputDate) => {
    const date = inputDate ? new Date(inputDate) : new Date();
    date.setHours(0, 0, 0, 0);
    return date;
};

const formatSnapshotName = (date, locale) => {
    try {
        return date.toLocaleDateString(locale || undefined, { dateStyle: 'short' });
    } catch {
        return date.toISOString().split('T')[0];
    }
};

// D2 — re-express a demo snapshot's stored figures when the display currency
// changes. Its own copies, not the live portfolio: a snapshot records what was
// true then, so it must not be rebuilt from today's items.
const scaleSnapshot = (snapshot, factor) => {
    const next = { ...snapshot };
    if (typeof next.liquidAssets === 'number') next.liquidAssets = next.liquidAssets * factor;
    next.portfolioItems = (snapshot.portfolioItems || []).map((item) => {
        const scaled = { ...item };
        DEMO_MONETARY_FIELDS.forEach((field) => {
            if (scaled[field] === null || scaled[field] === undefined || scaled[field] === '') return;
            const value = parseFloat(scaled[field]);
            if (!isNaN(value)) scaled[field] = value * factor;
        });
        return scaled;
    });
    return next;
};

// planGate: { isPro, onGateHit } — onGateHit('snapshots') opens the paywall
// when a free account tries to create past the free cap.
export const useSnapshots = (userId, isDemoMode, showModal, t, demoSeed = null, planGate = null) => {
    const [snapshots, setSnapshots] = useState([]);
    const [loading, setLoading] = useState(true);
    const [demoSnapshots, setDemoSnapshots] = useState([]);

    // D2 — the seed is applied ONCE per demo session. It used to re-run on
    // every change to `demoSeed`, which is rebuilt from the live demo items —
    // so creating a demo snapshot and then editing any item discarded the
    // snapshot. `seedCurrencyRef` lets a currency switch re-express the copies
    // instead.
    const demoSeededRef = useRef(false);
    const seedCurrencyRef = useRef(null);

    useEffect(() => {
        if (!isDemoMode) {
            demoSeededRef.current = false;
            seedCurrencyRef.current = null;
        }
    }, [isDemoMode]);

    useEffect(() => {
        if (isDemoMode && demoSeed && !demoSeededRef.current) {
            demoSeededRef.current = true;
            seedCurrencyRef.current = demoSeed.currency || null;
            const base = toStartOfDay(new Date());
            // `history` says both when each snapshot was taken and what the
            // portfolio was worth then. Without it a snapshot holds today's
            // portfolio at a past date, which is what left the demo's history
            // line flat until annotation pass 4; the fallback keeps this hook
            // working for any caller that seeds a portfolio and no history.
            const history = demoSeed.history && demoSeed.history.length
                ? demoSeed.history
                : [{ monthsAgo: 12 }, { monthsAgo: 6 }];
            const makeSnap = (past, idx) => {
                const date = toStartOfDay(new Date(base.getFullYear(), base.getMonth() - past.monthsAgo, base.getDate()));
                const name = formatSnapshotName(date, demoSeed.locale);
                // For demo snapshots we keep everything as JS Date objects.
                return {
                    id: `demo-snapshot-${idx + 1}`,
                    name,
                    snapshotDate: date,
                    createdAt: date,
                    lastUpdated: date,
                    updatedAt: date,
                    portfolioItems: past.items || demoSeed.items || [],
                    timelineEvents: demoSeed.timelineEvents || [],
                    liquidAssets: typeof past.liquidAssets === 'number' ? past.liquidAssets : (demoSeed.liquidAssets || 0),
                    liquidAssetsInterestRate: demoSeed.liquidAssetsInterestRate || 0,
                };
            };
            const seeded = history.map(makeSnap);
            setDemoSnapshots(seeded);
            setSnapshots(seeded);
            setLoading(false);
        }
    }, [isDemoMode, demoSeed]);

    // D2 — currency switch: re-express the demo's snapshots, including any the
    // visitor created, rather than reseeding them.
    useEffect(() => {
        if (!isDemoMode || !demoSeededRef.current || !demoSeed) return;
        const previous = seedCurrencyRef.current;
        const next = demoSeed.currency || null;
        if (!previous || !next || previous === next) return;
        seedCurrencyRef.current = next;
        const from = DEMO_CURRENCY_CONVERSION_RATES[previous] || 1;
        const to = DEMO_CURRENCY_CONVERSION_RATES[next] || 1;
        const factor = from / to;
        if (factor === 1) return;
        setDemoSnapshots(prev => prev.map(snapshot => scaleSnapshot(snapshot, factor)));
    }, [isDemoMode, demoSeed]);

    useEffect(() => {
        if (isDemoMode) {
            setSnapshots(demoSnapshots);
            setLoading(false);
            return;
        }
        if (!userId) {
            setSnapshots([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        const snapshotsPath = `artifacts/${appId}/users/${userId}/snapshots`;
        const q = query(collection(db, snapshotsPath), orderBy('createdAt', 'desc'));

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const docs = querySnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            setSnapshots(docs);
            setLoading(false);
        }, (error) => {
            console.error('Error fetching snapshots:', error);
            setLoading(false);
            if (showModal) {
                showModal(errorMessage(t, error));
            }
        });

        return () => unsubscribe();
    }, [userId, isDemoMode, demoSnapshots, showModal, t]);

    const enforceLimit = useCallback(async () => {
        if (!userId) {
            return false;
        }

        // D2 — the demo takes the SAME free cap as a real free account, on
        // purpose. Two seeded snapshots plus one the visitor makes is three, so
        // the next attempt meets the real paywall. Before this the gate was
        // unreachable from the demo, which meant the one place a visitor could
        // have seen the free tier's actual ceiling never showed it.

        // Freemium gate: free accounts create up to FREE_LIMITS.snapshots.
        // Snapshots already over the cap stay fully readable/restorable —
        // only creating new ones (incl. duplicating) is gated.
        if (!planGate?.isPro && snapshots.length >= FREE_LIMITS.snapshots) {
            if (planGate?.onGateHit) {
                planGate.onGateHit('snapshots');
            } else if (showModal) {
                showModal(t('snapshots.limitReached'));
            }
            return false;
        }

        if (snapshots.length >= PRO_SNAPSHOT_LIMIT) {
            if (showModal) {
                showModal(t('snapshots.limitReached', 'Snapshot limit reached. Please delete an existing snapshot before creating a new one.'));
            }
            return false;
        }

        return true;
    }, [snapshots, userId, showModal, t, planGate]);

    const createSnapshot = useCallback(async ({ portfolioItems = [], timelineEvents = [], liquidAssets = 0, liquidAssetsInterestRate = 0, date = null, locale, name }) => {
        const startOfDay = toStartOfDay(date);
        const snapshotDateTs = Timestamp.fromDate(startOfDay);
        const nowTs = Timestamp.now();
        let finalName = typeof name === 'string' ? name.trim() : '';
        if (!finalName) {
            finalName = formatSnapshotName(startOfDay, locale);
        }

        if (!userId) {
            return;
        }

        if (isDemoMode) {
            const canCreate = await enforceLimit();
            if (!canCreate) return;
            setDemoSnapshots(prev => [{
                id: `demo-snapshot-new-${Date.now()}`,
                name: finalName,
                snapshotDate: startOfDay,
                createdAt: new Date(),
                lastUpdated: new Date(),
                updatedAt: new Date(),
                portfolioItems,
                timelineEvents,
                liquidAssets,
                liquidAssetsInterestRate,
            }, ...prev]);
            return;
        }

        try {
            const canCreate = await enforceLimit();
            if (!canCreate) {
                return;
            }
            const snapshotsRef = collection(db, `artifacts/${appId}/users/${userId}/snapshots`);
            await addDoc(snapshotsRef, {
                name: finalName,
                snapshotDate: snapshotDateTs,
                createdAt: nowTs,
                lastUpdated: nowTs,
                updatedAt: nowTs,
                portfolioItems,
                timelineEvents,
                liquidAssets,
                liquidAssetsInterestRate,
            });
        } catch (error) {
            console.error('Error creating snapshot:', error);
            if (showModal) {
                showModal(errorMessage(t, error));
            }
        }
    }, [isDemoMode, userId, enforceLimit, showModal, t]);

    const updateSnapshot = useCallback(async (snapshotId, data) => {
        if (!snapshotId) return;

        if (isDemoMode) {
            // Renaming and re-dating work; writing PORTFOLIO CONTENT into a
            // demo snapshot is refused upstream (see `blockedBySnapshot` in
            // usePortfolio / useTimeline), so this only ever sees metadata.
            setDemoSnapshots(prev => prev.map(snapshot => {
                if (snapshot.id !== snapshotId) return snapshot;
                const next = { ...snapshot, ...data, lastUpdated: new Date(), updatedAt: new Date() };
                const effectiveDate = data.snapshotDate || data.createdAt;
                if (effectiveDate) {
                    next.snapshotDate = toStartOfDay(effectiveDate);
                    next.createdAt = snapshot.createdAt;
                }
                if (Object.prototype.hasOwnProperty.call(data, 'name') && data.name === '') {
                    delete next.name;
                }
                return next;
            }));
            return;
        }

        if (!userId) { return; }

        try {
            const snapshotRef = doc(db, `artifacts/${appId}/users/${userId}/snapshots`, snapshotId);
            const nowTs = Timestamp.now();
            let payload = { ...data, lastUpdated: nowTs, updatedAt: nowTs };

            if (data.snapshotDate) {
                const startOfDay = toStartOfDay(data.snapshotDate);
                payload.snapshotDate = Timestamp.fromDate(startOfDay);
            } else if (data.createdAt) {
                // Backwards-compatible: treat createdAt from callers as an effective date,
                // and map it into snapshotDate without overwriting the true creation time.
                const startOfDay = toStartOfDay(data.createdAt);
                payload.snapshotDate = Timestamp.fromDate(startOfDay);
                delete payload.createdAt;
            }

            if (Object.prototype.hasOwnProperty.call(data, 'name')) {
                if (data.name === '') {
                    // Clear custom name so UI falls back to date.
                    payload.name = deleteField();
                }
            } else {
                delete payload.name;
            }
            await updateDoc(snapshotRef, payload);
        } catch (error) {
            console.error('Error updating snapshot:', error);
            if (showModal) {
                showModal(errorMessage(t, error));
            }
        }
    }, [isDemoMode, userId, showModal, t]);

    const deleteSnapshot = useCallback(async (snapshotId) => {
        if (!snapshotId) return;

        if (isDemoMode) {
            setDemoSnapshots(prev => prev.filter(snapshot => snapshot.id !== snapshotId));
            return;
        }

        if (!userId) { return; }

        try {
            await deleteDoc(doc(db, `artifacts/${appId}/users/${userId}/snapshots`, snapshotId));
        } catch (error) {
            console.error('Error deleting snapshot:', error);
            if (showModal) {
                showModal(errorMessage(t, error));
            }
        }
    }, [isDemoMode, userId, showModal, t]);

    return { snapshots, loading, createSnapshot, updateSnapshot, deleteSnapshot };
};

export default useSnapshots;
